import React from 'react';
import { connect } from 'react-redux';
import { Redirect } from 'react-router-dom';
import { DateTime } from 'luxon';

import withStyles from '@mui/styles/withStyles';
import Grid from '@mui/material/Grid';

import { addMissive } from '../store/slices/missivesSlice';
import { addOutreaches } from '../store/slices/outreachListsSlice';
import { addOutreachesToFirmContact, addOutreachesFromFirmContact } from '../store/slices/outreachesSlice';
import { maybeUpdatePersonLatestOutreachInfo } from '../store/slices/personsSlice';
import { clearTaskAccomplishmentInfo } from '../store/slices/dashboardStatsSlice';
import { setApiResponseInfo } from '../store/slices/apiResponseInfoSlice';

import { createStdRequest, apiFetch } from '../utils/utils';

import DA from '../store/ReduxAccess';
import ReduxLoader from '../store/ReduxLoader';

import activityLoggingPanelStyles from '../assets/jss/crm/components/activityLoggingPanelStyles';

import CrmTextField from './CrmTextField';
import CrmButton from './CrmButton';
import KSelect from './KSelect';
import LabeledTimePicker from './LabeledTimePicker';
import KDatePicker from './KDatePicker';

/* Call with
  <ActivityLoggingPanel
    page={page}
    activityColumn={activityColumn}
    client={client}
    referrerOrgId={visibleFirmId}
    contacts={contacts}
  />
 */

class ActivityLoggingPanel extends React.Component {
  constructor(props) {
    super(props);
    const now = DateTime.local();
    const startOfToday = now.startOf('day');
    this.state = {
      activityType: 'Phone Call',
      activityDirection: 'To',
      activityPartner: 0,
      activityDescription: '',
      activityDate: startOfToday,
      activityTime: now.diff(startOfToday),
      isAutoDescription: false,
    };
  }

  componentDidMount() {
    this.selectFirstContact(this.props);
  }

  componentDidUpdate(prevProps) {
    if (this.props.contacts !== prevProps.contacts) {
      this.selectFirstContact(this.props);
    }
  }

  selectFirstContact(someProps) {
    const { contacts } = someProps;
    if (contacts && contacts.length > 0) {
      this.setState({ activityPartner: contacts[0].personId });
    } else {
      this.setState({ activityPartner: 0 });
    }
  }

  placeholderForSelectedActivityType() {
    return 'Describe ' + this.state.activityType;
  }

  handleSelectChange(event) {
    this.setState({ [event.target.name]: event.target.value });
  }

  handleActivityTypeChange(event) {
    this.setState({ activityType: event.target.value }, () => this.maybeAutoSupplyDescription());
  }

  handleActivityPartnerChange(event) {
    this.setState({ activityPartner: event.target.value }, () => this.maybeAutoSupplyDescription());
  }

  maybeAutoSupplyDescription() {
    if (this.isCandidateForAutoDescription(this.state.activityType)) {
      if (!this.state.activityDescription || this.state.isAutoDescription) {
        const descrip = this.generateAutoDescription();
        this.setState({ activityDescription: descrip, isAutoDescription: true });
      }
    } else {
      if (this.state.isAutoDescription) {
        this.setState({ activityDescription: '', isAutoDescription: false });
      }
    }
  }

  generateAutoDescription() {
    const activityType = this.state.activityType;
    const actvitiyPartnerId = this.state.activityPartner;
    const activityPartner = DA.getPersonById(actvitiyPartnerId);
    const partnerName = activityPartner.preferredName;
    let description = '';
    switch (activityType) {
      case 'Tour':
        description = 'Gave a tour to ' + partnerName;
        break;
      case 'Schedule Assessment':
        description = 'Scheduled Assessment with ' + partnerName;
        break;
      case 'Complete Assessment':
        description = partnerName + ' completed an Assessment';
        break;
      default:
        description = 'Did something with ' + partnerName;
        break;
    }
    return description;
  }

  isCandidateForAutoDescription(activityType) {
    return activityType === 'Tour' || activityType === 'Schedule Assessment' || activityType === 'Complete Assessment';
  }

  handleActivityDateChange(newDate) {
    this.setState({ activityDate: newDate });
  }

  handleActivityTimeChange(newDuration) {
    this.setState({ activityTime: newDuration });
  }

  cancelActivity() {
    this.clearDescription();
  }

  clearDescription() {
    this.setState({ activityDescription: '' });
  }

  submitActivity() {
    switch (this.state.activityType) {
      case 'Phone Call':
        this.submitLoggingOfActivity('PhoneCall', 'Phone Call');
        break;
      case 'Email':
        this.submitLoggingOfActivity('PersonalEmail', 'Email');
        break;
      case 'Voice Mail':
        this.submitLoggingOfActivity('PersonalVoiceMailOrMessage', 'Voice Mail');
        break;
      case 'Meeting':
        this.submitLoggingOfActivity('Meeting', 'Meeting');
        break;
      case 'Tour':
        this.submitLoggingOfActivity('Tour', 'Tour');
        break;
      case 'Schedule Assessment':
        this.submitLoggingOfActivity('ScheduleAssessment', 'Schedule Assessment');
        break;
      case 'Complete Assessment':
        this.submitLoggingOfActivity('CompleteAssessment', 'Complete Assessment');
        break;
      default:
        break;
    }
  }

  submitLoggingOfActivity(typeStr, missiveName) {
    const thisFirmId = DA.getFirmId();
    const apiStruct = this.buildApiStructForLoggingActivity(typeStr, missiveName);
    const uri = `/api/firm/${thisFirmId}/outreach`;
    const request = createStdRequest(uri, 'post', apiStruct);
    const actionOnGoodResponse = (json) => {
      this.props.clearTaskAccomplishmentInfo();
      this.clearDescription();
      const outreach = json;
      const missive = outreach.missive;
      this.props.addMissive(missive);
      const otherFirmId = this.props.referrerOrgId;
      const receiverId = this.state.activityPartner;
      const reduxStruct = ReduxLoader.convertOutreachStructIntoReduxStruct(outreach);
      if (otherFirmId && this.props.activityColumn) {
        // this ActivityLoggingPanel is being used by an Org page, rather than a Person page
        const direction = this.state.activityDirection;
        if (direction === 'To') {
          this.props.addOutreachesToFirmContact({ contactId: receiverId, outreaches: [reduxStruct] });
        } else {
          this.props.addOutreachesFromFirmContact({ contactId: receiverId, outreaches: [reduxStruct] });
        }
      } else {
        this.props.addOutreaches({ personId: receiverId, outreaches: [reduxStruct] });
      }
      if (this.props.activityColumn) {
        this.props.activityColumn.buildActivityGroupsFromReduxStore(otherFirmId);
      } else {
        const partyId = this.props.client ? this.props.client.personId : this.state.activityPartner;
        this.props.page.buildActivityGroupsFromReduxStore(partyId);
      }
      this.props.maybeUpdatePersonLatestOutreachInfo({
        personId: outreach.receiverId,
        date: outreach.date,
        description: missive.description,
      });
      this.setState({ activityDate: DateTime.local().startOf('day') });
    };
    apiFetch(this, request, actionOnGoodResponse);
  }

  buildApiStructForLoggingActivity(typeStr, missiveName) {
    const userPersonId = this.props.userInfo.personId;
    const thisFirmId = DA.getFirmId();
    const otherFirmId = this.props.referrerOrgId;
    const contactId = this.state.activityPartner;
    const date = this.state.activityDate.plus(this.state.activityTime);
    const direction = this.state.activityDirection;
    const senderId = direction === 'To' ? userPersonId : contactId;
    const senderFirmId = direction === 'To' ? thisFirmId : otherFirmId;
    const receiverId = direction === 'To' ? contactId : userPersonId;
    const receiverFirmId = direction === 'To' ? otherFirmId : thisFirmId;
    const missiveDescription = this.state.activityDescription.trim();
    const missiveContent = missiveDescription;
    return {
      outreachId: 0,
      date: date.toISO(),
      firmId: thisFirmId,
      senderId,
      senderFirmId,
      receiverId,
      receiverFirmId,
      dlId: 0,
      missiveId: 0,
      missive: {
        missiveId: 0,
        firmId: thisFirmId,
        creatorId: senderId,
        typeStr,
        name: missiveName,
        description: missiveDescription,
        content: missiveContent,
      },
    };
  }

  render() {
    if (this.state.redirect) {
      return <Redirect to={this.state.redirect} />;
    }

    const { classes, contacts } = this.props;
    const contactNames = contacts ? contacts.map((c) => c.preferredFullName) : [];
    const contactIds = contacts ? contacts.map((c) => c.personId) : [];

    const firstContactId = contactIds.length > 0 ? contactIds[0] : 0;
    const activityPartnerToShow = this.state.activityPartner ? this.state.activityPartner : firstContactId;

    const { dropdowns } = this.props;
    const activityTypeDropdownSpec = dropdowns.find((dd) => dd.name === 'Activity Type');
    const activityTypeDropdownOptions = activityTypeDropdownSpec.items.map((item) => item.text);
    const activityType = this.state.activityType;
    const requiresToFromChoice = !(
      activityType === 'Meeting' ||
      activityType === 'Tour' ||
      activityType === 'Schedule Assessment' ||
      activityType === 'Complete Assessment'
    );

    return (
      <div className={classes.activityLoggingPanel}>
        <Grid container spacing={2}>
          <Grid item xs={4}>
            <KSelect
              id="activity-select"
              name="activityType"
              fullWidth={true}
              liveOptions={activityTypeDropdownOptions}
              value={activityType}
              onChange={(event) => this.handleActivityTypeChange(event)}
            />
          </Grid>
          <Grid item xs={2}>
            {requiresToFromChoice ? (
              <KSelect
                id="activity-direction"
                name="activityDirection"
                fullWidth={true}
                liveOptions={['To', 'From']}
                value={this.state.activityDirection}
                onChange={(event) => this.handleSelectChange(event)}
              />
            ) : (
              <div className={classes.activityLiteralWithDiv}>With</div>
            )}
          </Grid>
          <Grid item xs={6}>
            <KSelect
              id="activity-partner"
              name="activityPartner"
              fullWidth={true}
              liveOptions={contactNames}
              liveValues={contactIds}
              value={activityPartnerToShow}
              onChange={(event) => this.handleActivityPartnerChange(event)}
            />
          </Grid>

          <Grid item xs={12}>
            <CrmTextField
              placeholder={this.placeholderForSelectedActivityType()}
              fullWidth
              multiline
              value={this.state.activityDescription}
              onChange={(event) => this.setState({ activityDescription: event.target.value, isAutoDescription: false })}
            />
          </Grid>

          {this.state.activityDescription.trim() && (
            <Grid item xl={5} lg={5} md={12}>
              <KDatePicker
                label="Date"
                value={this.state.activityDate || Date.today()}
                onChange={(newDate) => this.handleActivityDateChange(newDate)}
              />
            </Grid>
          )}

          {this.state.activityDescription.trim() && (
            <Grid item xl={3} lg={7} md={12}>
              <LabeledTimePicker
                label="Time"
                value={this.state.activityTime}
                onChange={(newDuration) => this.handleActivityTimeChange(newDuration)}
              />
            </Grid>
          )}

          {this.state.activityDescription.trim() && (
            <Grid item xl={4} lg={12} md={12} justifyContent={'flex-end'} style={{ display: 'flex' }}>
              <CrmButton outlined onClick={() => this.cancelActivity()}>
                Cancel
              </CrmButton>
              <CrmButton filled onClick={() => this.submitActivity()}>
                Save
              </CrmButton>
            </Grid>
          )}
        </Grid>
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  userInfo: state.userInfo,
  dropdowns: state.firmInfo.dropdowns,
});

const actionProducers = {
  setApiResponseInfo,
  addOutreaches,
  addOutreachesToFirmContact,
  addOutreachesFromFirmContact,
  addMissive,
  maybeUpdatePersonLatestOutreachInfo,
  clearTaskAccomplishmentInfo,
};

const styledClass = withStyles(activityLoggingPanelStyles)(ActivityLoggingPanel);
export default connect(mapStateToProps, actionProducers)(styledClass);
